import _ from 'lodash';

import Modal from './organization.modal.component';
import template from './organizations.component.html';

const PAGE = {
  home: 'admin.organizations',
  new: 'admin.organizations.new',
  org: 'admin.organizations.org',
  edit: 'admin.organizations.org.edit',
  // settings: 'admin.organizations.org.settings',
  // payment: 'admin.organizations.org.payment',
  // licenses: 'admin.organizations.org.licenses'
  schedules: 'admin.organizations.org.schedules'
};

/* @ngInject */
class Organizations {
  #$scope;

  constructor(
    $scope,
    $state,
    $stateParams,
    $transitions,
    GroupService,
    ModalService,
    Organization,
    LicenseTypeService,
    UserInfoService,
    growl,
    $sails
  ) {
    'ngInject';

    this.#$scope = $scope;
    this.$state = $state;
    this.$stateParams = $stateParams;
    this.$group = GroupService;
    this.$modal = ModalService;
    this.service = Organization;
    this.LicenseTypeService = LicenseTypeService;
    this.growl = growl;
    this.model = {};
    this.$sails = $sails;
    this.formOptions = {
      formType: 'organizations.settings',
      title: 'Security'
    };

    this.kind = 'Organizations';

    this.selectedLicenseDetails = [];

    this.lastActiveTab = 0;
    this.selected = undefined;
    this.menuVisible = false;
    /** Whether elements for super admins should be displayed to the user */
    this.userIsSuper = UserInfoService.isCurrentUserSuperAdmin();
    this.userOnlyAdmin = UserInfoService.isCurrentUserExactlyAdmin();

    if (this.userIsSuper) {
      this.licenseTypesGet = this.LicenseTypeService.model;
    }

    this.dueDate = {
      opened: false,
      open($event) {
        $event.preventDefault();
        $event.stopPropagation();
        this.opened = true;
      }
    };

    this.transitionHooks = [
      $transitions.onSuccess({ to: PAGE.home }, () => {
        if (this.userOnlyAdmin) {
          this.$state.go(`${PAGE.org}`, { id: this.organizations.array[0].id });
        } else {
          this.select();
        }
      }),
      $transitions.onSuccess({ to: `${PAGE.org}*` }, () => this.select($stateParams.id ||
        this.organizations.array[0].id)),
      $transitions.onSuccess({ to: PAGE.new }, () => !this.userOnlyAdmin && this.create()),
      $transitions.onSuccess({ to: PAGE.edit }, () => !this.userOnlyAdmin && this.edit()),
    ];

  }

  $onInit() {
    const stateName = this.$state.current.name.split('.').pop();
    this.lastActiveTab = { settings: 0, payment: 1, licenses: 2, schedules: 3 }[stateName] || 0;
    if (this.organizations.array.length === 1 && this.userOnlyAdmin) {
      this.formOptions.hideAdvanced = true;
      this.select(this.organizations.array[0].id);
      this.$stateParams.id = this.organizations.array[0].id;
    } else {
      this.select(this.$stateParams.id);
      // Hook equivalent for first load (when transitions not registered yet)
      switch (stateName) {
        case 'new':
          this.create();
          break;
        case 'edit':
          this.edit();
          break;
        default:
      }
    }

    this.closeContextMenu = () => {
      if (this.menuVisible) {
        this.menuVisible = false;
        this.#$scope.$apply();
      }
    };
    window.addEventListener('click', this.closeContextMenu);
  }

  async openModal(id) {
    if (id) {
      await this.select(id);
    }
    const groups = await this.$group.getGroups();
    return this.$modal.open({
      ...Modal,
      size: 'lg',
      resolve: {
        entity: () => id ? this.selected : undefined,
        groups: () => groups,
      }
    });
  }

  async select(id) {
    if (!id) {
      this.selected = undefined;
      return;
    }
    if (this.selected?.id === id) {
      return;
    }

    if (this.userIsSuper) {
      const { array } = await this.licenseTypesGet;
      this.licenseTypes = _.cloneDeep(array);
      const { data: licenses } = await this.service.licenses(id);
      const licenseCounts = _.keyBy(licenses, 'licenseType');
      _.forEach(this.licenseTypes, (licenseType) => {
        const licenseCount = licenseCounts[licenseType.id];
        licenseType.qty = licenseCount?.qty || 0;
        licenseType.used = licenseCount?.used || 0;
      });
    }
    this.selected = this.organizations.byId[id];
    this.model = _.cloneDeep(this.selected.settings) || {};
    this.payment = this.model.payment?.dueDate ?
      { ...this.model.payment, dueDate: new Date(this.model.payment.dueDate) } :
      {}
    ;

    delete this.model.payment;

    // Preserve tab state/route when switching between orgs
    this.openTab(this.lastActiveTab, this.selected);
  }

  onRowExpanded(license) {
    this.$sails.get('/license', {
      where: {
        licenseType: license.id,
        org: this.selected.id,
        [license.consumer]: { '!': null }
      },
      populate: license.consumer
    })
      .then(({ data: licenseDetails }) => {
        this.selectedLicenseDetails = _.filter(licenseDetails, license.consumer);
      });
  }

  handleSelect(org) {
    this.$state.go(...org?.id ? [PAGE.org, org] : [PAGE.home]);
  }

  async create() {
    try {
      const newEntity = await this.openModal();
      const { data: { id } } = await this.service.create(newEntity);
      this.openTab(0, id);
    } catch (err) {
      if (_.isError(err)) {
        console.error(err);
        this.growl.error(_.toString(err.error || err.message), { title: 'Problem in form:' });
      }
      this.selected ?
        this.openTab(this.lastActiveTab, this.selected) :
        this.$state.go(PAGE.home);
    } finally {
      this.$group.getGroupTree(true);
      this.#$scope.$apply();
    }
  }

  async edit() {
    const { selected } = this;
    if (!selected) {
      throw new Error('Attempting to edit without having selected anything');
    }

    try {
      const entity = await this.openModal(selected.id);
      await this.service.update(selected.id, entity);
    } catch (err) {
      if (_.isError(err)) {
        console.error(err);
        this.growl.error(_.toString(err.error || err.message), { title: 'Problem in form:' });
      }
    } finally {
      this.$group.getGroupTree(true);
      this.openTab();
    }
  }

  async destroy(id = this.selected.id) {
    const org = this.organizations.byId[id];
    try {
      await this.$modal.confirmationModal({
        modalTitle: `Delete ${org.name}`,
        typeToConfirm: org.name,
        optionalWarning: 'This will also result in the deletion of everything associated to this organization.',
        buttonText: 'Delete',
      });
      await this.service.destroy(id);
      this.$group.getGroupTree(true);
      this.$state.go(PAGE.home);
    } catch (err) {
      if (_.isError(err)) {
        console.error(err);
        this.growl.error(_.toString(err.error || err.message), { title: `Could not delete ${org.name}:` });
        this.$state.go(PAGE.org, this.selected);
      }
    }
  }

  updateSettings(update) {
    const { model } = update;
    this.#$scope.$apply(() => {
      // keep original reference so we don't cause an infinite change loop
      model !== this.model && angular.copy(model, this.model);
    });
  }

  async save() {
    if (!(this.payment.dueDate && this.payment.dueNotice)) {
      this.payment = {};
    }
    try {
      await this.service.update(this.selected.id, {
        ...this.selected,
        settings: { ...this.model, payment: this.payment }
      });
      this.growl.success('Saved successfully!');
    } catch (err) {
      if (_.isError(err)) {
        console.error(err);
        this.growl.error(_.toString(err.error || err.data?.error || err.message || err), { title: 'Problem saving:' });
      }
    }
  }

  onLicenseSave(license) {
    this.service.license(this.selected.id, license);
    // TODO: Model update works?
  }

  scheduleSelected(schedule) {
    if (this.$state.current.name === PAGE.schedules) {
      this.schedule = schedule;
      this.$state.go(PAGE.schedules, { id: this.$stateParams.id, schedule });
    }
  }

  openContextMenu(entity, { originalEvent: event }) {
    this.contextTarget = this.organizations.byId[entity];
    const menu = document.querySelector('.context-menu');

    const { pageX: left, pageY: top } = event;
    const style = _.mapValues({ left, top }, pos => `${pos}px`);
    _.merge(menu.style, style);
    this.menuVisible = true;
  }

  /**
   * Open a given tab for a given organization
   *
   * @param {number} [to=this.lastActiveTab] Tab index
   * @param {object | string} [of=this.$stateParams.id] Org
   */
  openTab(to = this.lastActiveTab, of = this.$stateParams.id) {
    const tabs = ['settings', 'payment', 'licenses', 'schedules'];
    if (_.isObject(of)) { of = of.id }

    this.schedule = this.$stateParams.schedule;

    this.lastActiveTab = to;
    this.$state.go(`${PAGE.org}.${tabs[this.lastActiveTab]}`, { id: of });
  }


  $onDestroy() {
    this.transitionHooks.forEach(unregister => unregister());
    window.removeEventListener('click', this.closeContextMenu);
  }
}

export default {
  name: 'organizations',
  template,
  controller: Organizations,
  bindings: {
    organizations: '<',
  }
};
